import '../style.css';
import './armory.css';
import { allVehicles, roundById, rounds as ALL_ROUNDS, roundsFor, type Vehicle } from './data';
import { penAt, type Round } from './pen';
import type { ArmorViewer, HoverInfo, Mode } from './viewer';
import type { bestShots } from './analysis';
import { comparePage, compareUrl, indexPage, pageTitle, parseArmoryRoute, roundsPage, vehiclePage, vehicleUrl } from './wiki';

const DEV = import.meta.env.DEV;
const vehicles = allVehicles(DEV);
const byId = (id: string) => vehicles.find((v) => v.id === id);
const route = parseArmoryRoute(location.pathname, location.search);
const main = document.getElementById('armory')!;
const esc = (s: string) => s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]!);
const pct = (x: number) => `${Math.round(x * 100)}%`;
/** three.js and the analysis load only on pages that draw a model; the index stays light. */
const loadLib = async () => ({ ...(await import('./viewer')), ...(await import('./analysis')) });
let lib!: Awaited<ReturnType<typeof loadLib>>;

// The prerender ships the page's markup; the dev server builds the same markup here. Compare pages
// always rebuild: an uncommon matchup is served the generic compare shell and names its pair in the path.
if (!main.children.length || route.kind === 'compare') {
  if (route.kind === 'index') main.innerHTML = indexPage(vehicles);
  if (route.kind === 'rounds') main.innerHTML = roundsPage(vehicles);
  if (route.kind === 'vehicle') main.innerHTML = byId(route.id) ? vehiclePage(byId(route.id)!, vehicles) : `<p class="missing">No vehicle called “${esc(route.id)}”. <a href="/squad/vehicles/">All vehicles</a></p>`;
  if (route.kind === 'compare') main.innerHTML = comparePage(route.a ? byId(route.a) ?? null : null, route.b ? byId(route.b) ?? null : null);
  const t = pageTitle(route, byId); document.title = t.title;
}
document.querySelectorAll<HTMLAnchorElement>('.bar nav a').forEach((a) => a.classList.toggle('on', a.pathname.startsWith('/squad/vehicles')));

// ---------------------------------------------------------------- shared UI pieces

/** Rounds to offer: the attacker's own guns first, then infantry AT, then every other gun in the game. */
function roundOptions(attacker: Vehicle | null, selected: string | null): string {
  const groups = roundsFor(attacker);
  const shown = new Set(groups.flatMap((g) => g.rounds.map((r) => r.id)));
  const others = ALL_ROUNDS.filter((r) => !shown.has(r.id) && (DEV || !r.id.startsWith('fobmap-')));
  if (others.length) groups.push({ group: 'Other vehicles', rounds: others });
  const label = (r: Round) => `${r.name} · ${Math.round(r.pen_mm ?? penAt(r, 0))} mm${r.vehicles?.length && attacker && !attacker.armor?.rounds.includes(r.id) ? ` (${r.vehicles.map((id) => byId(id)?.name ?? id).join(', ')})` : ''}`;
  return groups.map((g) => `<optgroup label="${esc(g.group)}">${g.rounds.map((r) => `<option value="${r.id}"${r.id === selected ? ' selected' : ''}>${esc(label(r))}</option>`).join('')}</optgroup>`).join('');
}
const defaultRound = (attacker: Vehicle | null): Round | null => {
  const own = (attacker?.armor?.rounds ?? []).map(roundById).filter((r): r is Round => !!r).sort((a, b) => penAt(b, 0) - penAt(a, 0));
  return own[0] ?? roundsFor(null)[0]?.rounds[0] ?? ALL_ROUNDS[0] ?? null;
};

function legend(host: HTMLElement) {
  const el = document.createElement('div'); el.className = 'legend'; host.appendChild(el);
  return (mode: Mode, round: Round | null, pen: number) => {
    el.innerHTML = mode === 'armor'
      ? `<div class="lg-t">Effective thickness from this view</div><div class="ramp" style="background:linear-gradient(90deg,${lib.RAMP.map(([, c]) => c).join(',')})"></div><div class="ramp-l">${lib.RAMP.filter((_, i) => i % 2 === 0).map(([mm]) => `<span>${mm}</span>`).join('')}<span>mm</span></div>`
      : `<div class="lg-t">${esc(round?.name ?? 'Round')} · ${Math.round(pen)} mm at this range</div><div class="sw"><i style="background:${lib.VERDICT_COLOURS.pen}"></i>Penetrates<i style="background:${lib.VERDICT_COLOURS.marginal}"></i>Marginal<i style="background:${lib.VERDICT_COLOURS.no}"></i>Stopped</div>`;
  };
}

function tooltip(host: HTMLElement) {
  const el = document.createElement('div'); el.className = 'tip'; el.hidden = true; host.appendChild(el);
  return (h: HoverInfo | null) => {
    if (!h) { el.hidden = true; return; }
    const p = h.plate;
    const shot = h.shot;
    const vd = shot ? { pen: 'Penetrates', marginal: 'Marginal', no: 'Stopped' }[shot.verdict] : '';
    el.innerHTML = `<b>${esc(p.label)}</b><div class="tip-g">
      <span>Plate</span><span>${p.thickness_mm ?? '—'} mm</span>
      <span>Angle</span><span>${Math.round(h.angle)}°</span>
      <span>Effective</span><span>${Number.isFinite(h.eff) ? Math.round(h.eff) : '—'} mm</span></div>
      ${!p.allowPen ? '<div class="tip-n">Cannot be penetrated</div>' : ''}
      ${shot ? `<div class="tip-v v-${shot.verdict}">${vd}${shot.decisive && shot.decisive !== p ? ` at ${esc(shot.decisive.label.toLowerCase())}` : ''}</div>
        ${shot.layers.length > 1 ? `<div class="tip-n">${shot.layers.map((l) => `${esc(l.plate.label)} ${Math.round(l.eff)}`).join(' → ')} mm</div>` : ''}
        ${shot.behind.length ? `<div class="tip-n hot">Reaches ${shot.behind.map((b) => esc(b.label.toLowerCase())).join(', ')}</div>` : ''}` : ''}`;
    el.hidden = false;
    const r = host.getBoundingClientRect();
    el.style.left = `${Math.min(h.x + 16, r.width - el.offsetWidth - 8)}px`;
    el.style.top = `${Math.min(h.y + 16, r.height - el.offsetHeight - 8)}px`;
  };
}

function shotsList(res: ReturnType<typeof bestShots>['ranked'], round: Round | null): string {
  if (!round) return '<p class="dim">No rounds in the export yet.</p>';
  const best = res.filter((r) => r.pen > 0.02);
  if (!best.length) return `<p class="verdict-none">${esc(round.name)} does not get through from any of the six standard angles. Look for gaps with the pointer, or bring something heavier.</p>`;
  return `<ol class="shots">${res.map((r) => {
    const main = r.plates.filter((p) => p.penShare > 0.5).slice(0, 2);
    return `<li class="${r.pen > 0.02 ? '' : 'dim'}" data-aspect="${r.aspect.key}">
      <button type="button" class="aspect-btn" data-aspect="${r.aspect.key}">${r.marker ? `<span class="mk mk-${r.aimKind}">${r.marker}</span>` : '<span class="mk mk-none"></span>'}<b>${r.aspect.label}</b><span class="bar-pct"><i style="width:${pct(r.pen)}"></i><i class="m" style="width:${pct(r.marginal)}"></i></span><span class="num">${pct(r.pen)}</span></button>
      <div class="why">${main.length ? `Through ${main.map((p) => esc(p.plate.label.toLowerCase())).join(' and ')}` : r.pen > 0.02 ? 'Through small gaps only' : 'Nothing gets through'}${r.ammo > 0.01 ? ` · <span class="hot">ammo rack on ${pct(r.ammo)} of hits</span>` : ''}${r.engine > 0.01 ? ` · engine ${pct(r.engine)}` : ''}</div></li>`;
  }).join('')}</ol><p class="small dim">Share of the silhouette each angle gets through, from ${lib.ASPECTS.length} standard angles. Numbers mark the aim point on the model.</p>`;
}

// ---------------------------------------------------------------- vehicle page

async function vehiclePageMount(v: Vehicle) {
  const stage = document.getElementById('stage-a')!;
  const controls = document.getElementById('controls')!;
  if (!v.armor) { controls.innerHTML = ''; return; }
  lib = await loadLib();
  const state = { mode: 'pen' as Mode, round: defaultRoundAgainst(v), range: 300, xray: false };
  controls.innerHTML = `
    <div class="seg" role="radiogroup" aria-label="Colour by"><button data-mode="armor">Armour</button><button data-mode="pen">Can it pen?</button></div>
    <label class="ctl">Shoot with<select id="round">${roundOptions(null, state.round?.id ?? null)}</select></label>
    <label class="ctl">Range <output id="range-o"></output><input id="range" type="range" min="0" max="2000" step="25" value="${state.range}"></label>
    <div class="ctl-row"><label class="check"><input type="checkbox" id="xray"> X-ray: ammo, engine, fuel</label></div>
    <div class="snaps">${lib.ASPECTS.map((a) => `<button data-aspect="${a.key}">${a.label}</button>`).join('')}</div>
    <h2 class="shots-h">Where to aim</h2><div id="shots" class="shots-wrap"><p class="dim">Working it out…</p></div>
    <label class="ctl">Compare with<select id="vs"><option value="">Pick a vehicle…</option>${vehicles.filter((x) => x.id !== v.id && x.armor).map((x) => `<option value="${x.id}">${esc(x.name)}</option>`).join('')}</select></label>`;
  stage.innerHTML = '<div class="hint">Drag to turn · the view is the shot</div>';
  const viewer = new lib.ArmorViewer(stage);
  const setLegend = legend(stage);
  viewer.onHover(tooltip(stage));
  await viewer.load(v.armor.file, v.armor.plates);
  const $ = <T extends HTMLElement>(id: string) => document.getElementById(id) as T;
  let timer = 0;
  const update = () => {
    const pen = state.round ? penAt(state.round, state.range) : 0;
    viewer.setMode(state.mode); viewer.setPen(pen); viewer.setXray(state.xray);
    controls.querySelectorAll<HTMLButtonElement>('.seg button').forEach((b) => b.classList.toggle('on', b.dataset.mode === state.mode));
    $('range-o').textContent = `${state.range} m · ${Math.round(pen)} mm`;
    setLegend(state.mode, state.round, pen);
    clearTimeout(timer);
    timer = window.setTimeout(() => {
      const { ranked, markers } = lib.bestShots(lib.analyse(viewer, pen));
      $('shots').innerHTML = shotsList(ranked, state.round);
      viewer.setMarkers(state.mode === 'pen' ? markers : []);
    }, 60);
  };
  controls.addEventListener('click', (e) => {
    const b = (e.target as HTMLElement).closest<HTMLButtonElement>('button'); if (!b) return;
    if (b.dataset.mode) { state.mode = b.dataset.mode as Mode; update(); }
    if (b.dataset.aspect) viewer.lookAlong(lib.ASPECTS.find((a) => a.key === b.dataset.aspect)!.dir);
  });
  $<HTMLSelectElement>('round').addEventListener('change', (e) => { state.round = roundById((e.target as HTMLSelectElement).value); state.mode = 'pen'; update(); });
  $<HTMLInputElement>('range').addEventListener('input', (e) => { state.range = Number((e.target as HTMLInputElement).value); update(); });
  $<HTMLInputElement>('xray').addEventListener('change', (e) => { state.xray = (e.target as HTMLInputElement).checked; update(); });
  $<HTMLSelectElement>('vs').addEventListener('change', (e) => { const id = (e.target as HTMLSelectElement).value; if (id) location.assign(compareUrl(v.id, id)); });
  stage.querySelector('.stage-loading')?.remove();
  update();
}
/** A sensible first round to show against a vehicle: the strongest gun of its usual opposite number. */
function defaultRoundAgainst(v: Vehicle): Round | null {
  const foe = vehicles.find((x) => x.armor && x.id !== v.id && x.class === v.class && (v.fixture ? x.fixture : !x.fixture))
    ?? vehicles.find((x) => x.armor && x.id !== v.id);
  return defaultRound(foe ?? null);
}

// ---------------------------------------------------------------- compare page

async function comparePageMount(a0: Vehicle | null, b0: Vehicle | null) {
  lib = await loadLib();
  const controls = document.getElementById('controls')!;
  const pool = vehicles.filter((v) => v.armor);
  const a = a0 ?? pool[0] ?? null, b = b0 ?? pool.find((v) => v !== a) ?? null;
  const state = { range: 300, linked: true, xray: false, ra: defaultRound(a), rb: defaultRound(b) };
  const pick = (k: 'a' | 'b', cur: Vehicle | null) => `<select class="vsel" data-side="${k}">${[...vehicles].sort((x, y) => Number(!!y.armor) - Number(!!x.armor)).map((v) => `<option value="${v.id}"${v === cur ? ' selected' : ''}>${esc(v.name)}${v.armor ? '' : ' (no armour yet)'}</option>`).join('')}</select>`;
  for (const [k, v] of [['a', a], ['b', b]] as const) {
    const head = document.getElementById(`head-${k}`)!;
    head.innerHTML = `<span class="side-tag t${k === 'a' ? 1 : 2}">${k.toUpperCase()}</span>${pick(k, v)}${v ? `<a class="small" href="${vehicleUrl(v.id)}">wiki</a>` : ''}`;
  }
  document.querySelectorAll<HTMLSelectElement>('.vsel').forEach((s) => s.addEventListener('change', () => {
    const na = s.dataset.side === 'a' ? s.value : a?.id, nb = s.dataset.side === 'b' ? s.value : b?.id;
    if (na && nb) location.assign(compareUrl(na, nb));
  }));
  if (!a?.armor || !b?.armor) {
    // Nothing to shoot at yet: say so instead of showing empty round pickers.
    controls.innerHTML = `<p class="dim">Side-by-side armour opens once ${[a, b].filter((v) => v && !v.armor).map((v) => esc(v!.name)).join(' and ')} ${[a, b].filter((v) => v && !v.armor).length > 1 ? 'have' : 'has'} been exported from the Squad SDK. Pick other vehicles above, or see each vehicle's page for its factions and respawn.</p>`;
    return;
  }
  controls.innerHTML = `
    <label class="ctl"><span><span class="side-tag t1">A</span> ${esc(a?.name ?? '')} fires</span><select id="ra">${roundOptions(a, state.ra?.id ?? null)}</select></label>
    <label class="ctl"><span><span class="side-tag t2">B</span> ${esc(b?.name ?? '')} fires</span><select id="rb">${roundOptions(b, state.rb?.id ?? null)}</select></label>
    <label class="ctl">Range <output id="range-o"></output><input id="range" type="range" min="0" max="2000" step="25" value="${state.range}"></label>
    <div class="ctl-row"><label class="check"><input type="checkbox" id="linked" checked> Turn both together</label><label class="check"><input type="checkbox" id="xray"> X-ray</label></div>
    <div class="snaps">${lib.ASPECTS.map((x) => `<button data-aspect="${x.key}">${x.label}</button>`).join('')}</div>
    <h2 class="shots-h"><span class="side-tag t1">A</span> on <span class="side-tag t2">B</span>: where to aim</h2><div id="shots-ab" class="shots-wrap"></div>
    <h2 class="shots-h"><span class="side-tag t2">B</span> on <span class="side-tag t1">A</span>: where to aim</h2><div id="shots-ba" class="shots-wrap"></div>`;
  const mk = async (k: 'a' | 'b', v: Vehicle) => {
    const host = document.getElementById(`view-${k}`)!;
    host.innerHTML = '';
    const viewer = new lib.ArmorViewer(host);
    viewer.onHover(tooltip(host));
    await viewer.load(v.armor!.file, v.armor!.plates);
    return { viewer, legend: legend(host) };
  };
  const [A, B] = await Promise.all([mk('a', a), mk('b', b)]);
  // Same angle on both: what A sees of B is what B sees of A, so the two plans read together.
  let echo = false;
  const link = (src: ArmorViewer, dst: ArmorViewer) => src.onCamera((az, pol) => { if (!state.linked || echo) return; echo = true; dst.setView(az, pol); echo = false; });
  link(A.viewer, B.viewer); link(B.viewer, A.viewer);
  const $ = <T extends HTMLElement>(id: string) => document.getElementById(id) as T;
  let timer = 0;
  const update = () => {
    const penA = state.ra ? penAt(state.ra, state.range) : 0, penB = state.rb ? penAt(state.rb, state.range) : 0;
    // B's model is coloured by A's gun, and the other way round.
    B.viewer.setMode('pen'); B.viewer.setPen(penA); B.viewer.setXray(state.xray); B.legend('pen', state.ra, penA);
    A.viewer.setMode('pen'); A.viewer.setPen(penB); A.viewer.setXray(state.xray); A.legend('pen', state.rb, penB);
    $('range-o').textContent = `${state.range} m · A ${Math.round(penA)} mm · B ${Math.round(penB)} mm`;
    clearTimeout(timer);
    timer = window.setTimeout(() => {
      const ab = lib.bestShots(lib.analyse(B.viewer, penA)), ba = lib.bestShots(lib.analyse(A.viewer, penB));
      $('shots-ab').innerHTML = shotsList(ab.ranked, state.ra); B.viewer.setMarkers(ab.markers);
      $('shots-ba').innerHTML = shotsList(ba.ranked, state.rb); A.viewer.setMarkers(ba.markers);
    }, 60);
  };
  $<HTMLSelectElement>('ra').addEventListener('change', (e) => { state.ra = roundById((e.target as HTMLSelectElement).value); update(); });
  $<HTMLSelectElement>('rb').addEventListener('change', (e) => { state.rb = roundById((e.target as HTMLSelectElement).value); update(); });
  $<HTMLInputElement>('range').addEventListener('input', (e) => { state.range = Number((e.target as HTMLInputElement).value); update(); });
  $<HTMLInputElement>('linked').addEventListener('change', (e) => { state.linked = (e.target as HTMLInputElement).checked; });
  $<HTMLInputElement>('xray').addEventListener('change', (e) => { state.xray = (e.target as HTMLInputElement).checked; update(); });
  controls.addEventListener('click', (e) => {
    const btn = (e.target as HTMLElement).closest<HTMLButtonElement>('button[data-aspect]'); if (!btn) return;
    const asp = lib.ASPECTS.find((x) => x.key === btn.dataset.aspect)!;
    const inAb = !!btn.closest('#shots-ab'), inBa = !!btn.closest('#shots-ba');
    if (!inBa) B.viewer.lookAlong(asp.dir);
    if (!inAb) A.viewer.lookAlong(asp.dir);
  });
  update();
}

// ---------------------------------------------------------------- index page

function indexPageMount() {
  const rows = [...document.querySelectorAll<HTMLTableRowElement>('.vlist tbody tr')];
  const f = { faction: document.getElementById('f-faction') as HTMLSelectElement, cls: document.getElementById('f-class') as HTMLSelectElement, q: document.getElementById('f-q') as HTMLInputElement, armor: document.getElementById('f-armor') as HTMLInputElement };
  const apply = () => {
    const q = f.q.value.trim().toLowerCase();
    for (const r of rows) r.hidden = !!((f.faction.value && !r.dataset.f!.split(' ').includes(f.faction.value)) || (q && !r.dataset.q!.includes(q)) || (f.armor.checked && r.dataset.armor !== '1'));
    document.querySelectorAll<HTMLElement>('.iclass').forEach((s) => { s.hidden = (!!f.cls.value && s.dataset.class !== f.cls.value) || ![...s.querySelectorAll('tbody tr')].some((r) => !(r as HTMLElement).hidden); });
  };
  for (const el of Object.values(f)) el?.addEventListener('input', apply);
  document.getElementById('compare-form')?.addEventListener('submit', (e) => {
    e.preventDefault();
    const a = (document.getElementById('cmp-a') as HTMLSelectElement).value, b = (document.getElementById('cmp-b') as HTMLSelectElement).value;
    if (a && b && a !== b) location.assign(compareUrl(a, b));
  });
}

if (route.kind === 'index') indexPageMount();
if (route.kind === 'vehicle') { const v = byId(route.id); if (v) void vehiclePageMount(v); }
if (route.kind === 'compare') void comparePageMount(route.a ? byId(route.a) ?? null : null, route.b ? byId(route.b) ?? null : null);
